"use client"

import { useState, useRef, useEffect } from "react"
// ✅ Working relative paths
import Footer from "./Footer"
import ThreeSphere from "../AMyteSphere/ThreeSphere"
import { useFastTypewriter } from "../../hooks/useFastTypewriter"

import {
  Lightbulb,
  Key,
  Clock,
  Brain,
  Compass,
  Hammer,
  Users,
  MessageSquare,
  Layers,
  Zap,
  Code,
  Sparkles,
  Workflow,
  Rocket,
  CheckCircle,
  ArrowRight,
  Lock,
  Play,
  Award,
  Flame,
  X,
  Menu,
  ChevronUp,
} from "lucide-react"

const MyteAITrainingCourses = () => {
  // State management
  const [activeSection,  ] = useState("")
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [ , setShowTagline] = useState(false)
  const [isVisible, setIsVisible] = useState({})
  const [showStickyCTA, setShowStickyCTA] = useState(false)
  const [userTrack, setUserTrack] = useState("individual") // 'individual' or 'enterprise'

  // Refs
  const typedElementRef = useRef(null)
  const sectionRefs = useRef({})
  const heroRef = useRef(null)

  // Initialize typewriter effect
  useFastTypewriter(typedElementRef, () => setShowTagline(true))

  // Intersection observer for scroll animations
  useEffect(() => {
    const observers = {}

    Object.keys(sectionRefs.current).forEach((sectionId) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [sectionId]: true }))
            observer.disconnect()
          }
        },
        { threshold: 0.2 },
      )

      if (sectionRefs.current[sectionId]) {
        observer.observe(sectionRefs.current[sectionId])
        observers[sectionId] = observer
      }
    })

    return () => {
      Object.values(observers).forEach((observer) => observer.disconnect())
    }
  }, [])

  // Sticky CTA observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyCTA(!entry.isIntersecting)
      },
      { threshold: 0.2 },
    )

    if (heroRef.current) {
      observer.observe(heroRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  // Register a section ref
  const registerSectionRef = (id, ref) => {
    sectionRefs.current[id] = ref
  }

  // Course highlights from transcript
  const courseHighlights = [
    {
      icon: <Lightbulb className="w-9 h-9 text-[#3080c1]" />,
      text: "This isn't a software course. This is a journey in how to think and orchestrate with AI.",
    },
    {
      icon: <Key className="w-9 h-9 text-[#3080c1]" />,
      text: "Knowledge is power. The more clarity you feed the model, the more powerful the output.",
    },
    {
      icon: <Clock className="w-9 h-9 text-[#3080c1]" />,
      text: "We're building a system to give you back your time, letting AI handle the grunt work.",
    },
    {
      icon: <Brain className="w-9 h-9 text-[#3080c1]" />,
      text: "The interface adapts to you; you just have to lend it your vision and structure.",
    },
  ]

  // Course modules (simplified for teaser)
  const courseModules = [
    {
      title: "Foundations of AI Thinking",
      description: "Learn how AI mirrors your thinking and how to structure your thoughts for optimal results",
      icon: <Brain className="w-8 h-8 text-[#3080c1]" />,
    },
    {
      title: "Prompt Engineering Mastery",
      description: "Develop the skills to craft prompts that yield powerful, precise results",
      icon: <Code className="w-8 h-8 text-[#3080c1]" />,
    },
    {
      title: "System Design with AI",
      description: "Learn to build modular, scalable systems that leverage AI's capabilities",
      icon: <Workflow className="w-8 h-8 text-[#3080c1]" />,
    },
    {
      title: "Practical Applications",
      description: "Apply your skills to real-world scenarios across various domains",
      icon: <Rocket className="w-8 h-8 text-[#3080c1]" />,
    },
  ]

  // Testimonial quotes from the transcript
 const testimonialQuotes = [
  {
    quote: "I've missed a lot of damn rides and I'm not missing this one. I see it, and I'm not missing it.",
    author: "Jason, Course Participant",
    image: "/testimonials/jason.jpg",
  },
  {
    quote: "I'm excited to learn how to utilize this resource and get the most out of it.",
    author: "Tyler, Course Participant",
    image: "/testimonials/tyler.jpg",
  },
   {
    quote: "I use it quite often for stuff in school and working on coding projects. It's very helpful.",
    author: "Alex, Course Participant",
    image: "/testimonials/alex.jpg",
  },
  {
    quote: "I thought I knew AI. Now I realize I was just scratching the surface.",
    author: "Vince, Entrepreneur",
    image: "/testimonials/vince.jpg",
  },
  {
  quote: "You feel it become more personal as you're communicating with another entity.",
  author: "Jason, Course Participant",
  },
  {
  quote: "By the time this was done, I was talking way more freely than when I started.",
  author: "Jason, Course Participant",
  },
  {
  quote: "I can’t believe how fast I moved from zero to building something real.",
  author: "Tyler, Course Participant",
  },
 ]


  // Discovery spark demo slides
  const demoSlides = [
    {
      title: "Input Your Prompt",
      content: (
        <div className="relative">
          <div className="bg-white/10 p-4 rounded-lg border border-[#3080c1]/30 backdrop-blur-sm">
            <p className="text-blue-200 font-mono text-sm">
              <span className="text-[#3080c1] font-bold">Goal:</span> Create a training plan for my team
              <br />
              <span className="text-[#3080c1] font-bold">Context:</span> We need to implement a new workflow...
              <br />
              <span className="text-[#3080c1] font-bold">Constraints:</span> Limited time, diverse skill levels...
              <br />
              <span className="text-[#3080c1] font-bold">Output:</span> Detailed 4-week training schedule with...
              <br />
              <span className="text-[#3080c1] font-bold">Cycle:</span> First reflect on best practices, then...
            </p>
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0e1d34]/60 via-[#0e1d34]/70 to-[#0e1d34]/90 backdrop-blur-md flex items-center justify-center">
            <Lock className="w-10 h-10 text-[#3080c1]" />
          </div>
        </div>
      ),
    },
    {
      title: "AI Transforms Your Input",
      content: (
        <div className="flex justify-center">
          <div className="relative w-16 h-16 my-4">
            <div className="absolute inset-0 bg-[#3080c1]/20 rounded-full animate-ping"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-[#3080c1]" />
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Get Powerful Results",
      content: (
        <div className="bg-white/10 p-4 rounded-lg border border-[#3080c1]/30">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-5 h-5 text-[#3080c1]" />
            <h4 className="text-white font-semibold">Complete Training Plan</h4>
          </div>
          <div className="relative">
            <div className="bg-white/5 p-3 rounded border border-white/10 text-blue-200 text-sm">
              <p>Week 1: Foundations & Mindset</p>
              <p>Week 2: Practical Applications</p>
              <p>Week 3: Advanced Techniques</p>
              <p>Week 4: Integration & Mastery</p>
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-[#0e1d34]/60 via-[#0e1d34]/70 to-[#0e1d34]/90 backdrop-blur-md flex items-center justify-center">
              <Lock className="w-10 h-10 text-[#3080c1]" />
            </div>
          </div>
        </div>
      ),
    },
  ]

  // Custom navigation for this page
  const customNavLinks = [
    { name: "Home", href: "/" },
    { name: "Why It Matters", href: "#why-it-matters" },
    { name: "What You'll Gain", href: "#what-youll-gain" },
    { name: "Who It's For", href: "#who" },
    { name: "Reserve Your Spot", href: "#reserve" },
  ]

  return (
    <div className="bg-gradient-to-br from-[#0e1d34] to-[#1e3a8a] text-white min-h-screen flex flex-col">
      {/* Custom Header with conversion-focused navigation */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#0e1d34]/90 backdrop-blur-md shadow-lg`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          {/* Logo */}
          <div className="flex justify-center">
            <a href="/" className="flex items-center">
              <img
                src="/LogoFile.png"
                alt="Myte Group Logo"
                className="w-28 sm:w-36 h-auto max-w-full object-contain"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4 lg:space-x-6">
            {customNavLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className={`text-sm font-medium transition-colors duration-300 ${
                  activeSection === link.href.substring(1) ? "text-white" : "text-blue-200"
                } hover:text-white`}
              >
                {link.name}
              </a>
            ))}
            <a
              href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#3080c1] hover:bg-[#246399] text-white px-4 py-2 rounded-md transition-all duration-300"
            >
              Book a Strategic Training Call
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-white bg-transparent border-none p-1"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-[#0e1d34]/95 backdrop-blur-md p-4 animate-fadeIn">
            <nav className="flex flex-col gap-4">
              {customNavLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-lg text-blue-200 hover:text-white transition-colors duration-300"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              <a
                href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMenuOpen(false)}
                className="bg-[#3080c1] hover:bg-[#246399] text-white w-full mt-2 px-4 py-2 rounded-md text-center transition-all duration-300"
              >
                Reserve Your Spot
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Sticky CTA Banner */}
      {showStickyCTA && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0e1d34]/95 backdrop-blur-md border-t border-[#3080c1]/30 shadow-lg transform transition-transform duration-300 ease-in-out">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-between">
            <div className="flex items-center mb-2 sm:mb-0">
              <Play className="w-5 h-5 text-[#3080c1] mr-2" />
              <span className="text-white font-medium">Ready to transform your AI skills?</span>
            </div>
            <div className="flex gap-3">
              <a
                href="/joinmyte"
                className="text-sm bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-md transition-all duration-300 border border-white/20"
              >
                Get Free Course Deck
              </a>
              <a
                href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm bg-[#3080c1] hover:bg-[#246399] text-white px-4 py-2 rounded-md transition-all duration-300"
              >
                Book Discovery Call
              </a>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1">
        {/* HERO SECTION */}
        <section
          id="hero"
          ref={(el) => {
            registerSectionRef("hero", el)
            heroRef.current = el
          }}
          className="pt-40 pb-8 px-4 sm:px-6 bg-gradient-to-b from-[#0e1d34] to-[#1e3a8a] overflow-hidden min-h-[600px] lg:min-h-[650px] relative"
        >
          <div className="max-w-7xl mx-auto relative h-full">
            <div className="flex flex-col lg:flex-row items-center justify-between h-full">
              {/* Left: Text Content */}
              <div className="w-full lg:w-[66%] text-center lg:text-left z-20">
                <span className="bg-[#3080c1]/20 text-[#3080c1] rounded-full px-4 py-1 text-sm font-medium">
                  Build your interface. Mirror your mind.
                </span>

                <h1 className="mt-4 text-4xl md:text-6xl font-bold text-white leading-tight">
                  A New Kind of AI Course
                </h1>

                <p className="text-xl md:text-2xl text-blue-200 mt-4 mb-2 leading-snug max-w-2xl mx-auto lg:mx-0">
                  Learn to think in systems, not just prompts.
                </p>

                <p className="text-base text-blue-100 mt-2 max-w-xl mx-auto lg:mx-0">
                  AI isn't coming. It's here. This is your moment to build clarity, not dependency.
                </p>

                {/* Typewriter Dynamic Tagline */}
                <div className="h-12 text-2xl text-[#3080c1] font-semibold my-4">
                  <span ref={typedElementRef}></span>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-wrap sm:flex-nowrap gap-4 justify-center lg:justify-start">
                  <a
                    href="/joinmyte"
                    className="border border-[#3080c1] px-6 py-3 rounded-lg text-[#3080c1] hover:bg-[#3080c1] hover:text-white transition duration-300 text-center flex items-center gap-2"
                  >
                    <Play className="w-5 h-5" /> 🔥 Claim Your Free 22-Slide Course Deck
                  </a>

                  <a
                    href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#3080c1] hover:bg-[#246399] text-white px-6 py-3 rounded-lg transition duration-300 flex items-center gap-2 justify-center"
                  >
                    🏢 Book Enterprise Discovery Call
                  </a>
                </div>

                {/* What's Inside the Freebie */}
                <p className="text-sm text-blue-100 mt-2 lg:mt-1 max-w-xl mx-auto lg:mx-0">
                  Includes: AI Thinking Framework, Real Prompt Blueprints, Slide-by-Slide Prompting Flow.
                </p>

                {/* Above-the-fold testimonial */}
                <div className="mt-6 text-sm text-blue-200 italic max-w-xl mx-auto lg:mx-0">
                  "I have no words for the potential that this is giving me! Rock and roll!" – Jason Fussel Ironworker Local 29
                </div>
              </div>

              {/* Right: Sphere Visual */}
              <div className="relative mt-8 lg:mt-0 lg:absolute lg:right-0 lg:top-16 lg:bottom-0 lg:w-[33%] lg:h-full group overflow-visible">
                <div className="relative w-[320px] h-[360px] sm:w-[360px] sm:h-[400px] md:w-[420px] md:h-[440px] lg:h-[500px] lg:w-[500px] mx-auto lg:mx-0 -translate-y-12 sm:-translate-y-16 md:-translate-y-24 lg:-translate-y-32 flex items-center justify-center">
                  {/* Pulsing ring */}
                  <div className="absolute inset-0 rounded-full border-4 border-[#3080c1]/30 animate-ping opacity-30"></div>

                  {/* Glowing blur */}
                  <div className="absolute inset-0 rounded-full bg-[#3080c1]/10 blur-xl group-hover:bg-[#3080c1]/20 transition-all duration-500"></div>

                  {/* Sphere */}
                  <div className="relative z-10 w-full h-full transition-transform duration-500 group-hover:scale-105 flex items-center justify-center">
                    <ThreeSphere zoomMultiplier={8} noAutoTransition />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY IT MATTERS SECTION */}
        <section
          id="why-it-matters"
          className="py-16 px-4 sm:px-8 bg-[#f0f4f9] text-[#0e1d34]"
          ref={(el) => registerSectionRef("why-it-matters", el)}
        >
          <div className="max-w-6xl mx-auto">
            <h2 className={`text-3xl font-bold mb-6 ${isVisible["why-it-matters"] ? "animate-fadeIn" : "opacity-0"}`}>
              The World Has Changed
            </h2>
            <div
              className={`grid grid-cols-1 md:grid-cols-2 gap-8 items-center ${isVisible["why-it-matters"] ? "animate-fadeInUp" : "opacity-0"}`}
              style={{ animationDelay: "0.2s" }}
            >
              <div>
                <p className="text-lg text-gray-700 mb-4">
                  This course is about building <strong>clarity</strong> into your thinking so that your technology
                  reflects it. You'll learn how to structure prompts, workflows, and systems in a way that makes AI your
                  co-creator—not just your assistant.
                </p>
                <p className="text-lg text-gray-700">
                  By the end, you won't just "use AI"—you'll <strong>command</strong> it with intention, architecture,
                  and autonomy.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
                <h3 className="text-xl font-bold mb-4 text-[#3080c1]">What Makes This Course Different</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#3080c1] mt-0.5 mr-2 flex-shrink-0" />
                    <span>Focus on thinking systems, not just prompt tricks</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#3080c1] mt-0.5 mr-2 flex-shrink-0" />
                    <span>Build your own AI operating system that evolves with you</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#3080c1] mt-0.5 mr-2 flex-shrink-0" />
                    <span>Hands-on exercises with immediate practical applications</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#3080c1] mt-0.5 mr-2 flex-shrink-0" />
                    <span>Learn to build systems that mirror your unique thinking</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CHOOSE YOUR TRACK SECTION */}
        <section
          id="choose-track"
          className="py-16 px-4 sm:px-8 bg-white text-[#0e1d34]"
          ref={(el) => registerSectionRef("choose-track", el)}
        >
          <div className="max-w-6xl mx-auto">
            <h2
              className={`text-3xl font-bold mb-6 text-center ${isVisible["choose-track"] ? "animate-fadeIn" : "opacity-0"}`}
            >
              Choose Your Track
            </h2>
            <p
              className={`text-lg text-gray-700 max-w-3xl mx-auto mb-10 text-center ${isVisible["choose-track"] ? "animate-fadeIn" : "opacity-0"}`}
              style={{ animationDelay: "0.2s" }}
            >
              Select the path that best fits your goals and needs
            </p>

            <div
              className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${isVisible["choose-track"] ? "animate-fadeInUp" : "opacity-0"}`}
              style={{ animationDelay: "0.3s" }}
            >
              {/* Individual Track */}
              <div
                className={`p-8 rounded-xl shadow-md border-2 transition-all duration-300 cursor-pointer
                  ${
                    userTrack === "individual"
                      ? "border-[#3080c1] bg-[#3080c1]/5"
                      : "border-gray-200 hover:border-[#3080c1]/50 hover:bg-[#3080c1]/5"
                  }`}
                onClick={() => setUserTrack("individual")}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold">Individual</h3>
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center
                    ${userTrack === "individual" ? "border-[#3080c1] bg-[#3080c1]/20" : "border-gray-300"}`}
                  >
                    {userTrack === "individual" && <div className="w-3 h-3 rounded-full bg-[#3080c1]"></div>}
                  </div>
                </div>

                <p className="text-gray-700 mb-4">
                  Perfect for explorers, creators, and solopreneurs who want to build their personal AI operating
                  system.
                </p>

                <ul className="space-y-2 mb-6">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#3080c1] mt-0.5 mr-2 flex-shrink-0" />
                    <span>Self-paced learning materials</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#3080c1] mt-0.5 mr-2 flex-shrink-0" />
                    <span>Personal AI system development</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#3080c1] mt-0.5 mr-2 flex-shrink-0" />
                    <span>Focused on individual productivity</span>
                  </li>
                </ul>

                <a
                  href="/joinmyte"
                  className={`w-full block text-center py-3 rounded-lg transition duration-300
                    ${
                      userTrack === "individual"
                        ? "bg-[#3080c1] text-white hover:bg-[#246399]"
                        : "border border-[#3080c1] text-[#3080c1] hover:bg-[#3080c1] hover:text-white"
                    }`}
                >
                  Get Free Course Deck
                </a>
              </div>

              {/* Enterprise Track */}
              <div
                className={`p-8 rounded-xl shadow-md border-2 transition-all duration-300 cursor-pointer
                  ${
                    userTrack === "enterprise"
                      ? "border-[#3080c1] bg-[#3080c1]/5"
                      : "border-gray-200 hover:border-[#3080c1]/50 hover:bg-[#3080c1]/5"
                  }`}
                onClick={() => setUserTrack("enterprise")}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold">Enterprise</h3>
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center
                    ${userTrack === "enterprise" ? "border-[#3080c1] bg-[#3080c1]/20" : "border-gray-300"}`}
                  >
                    {userTrack === "enterprise" && <div className="w-3 h-3 rounded-full bg-[#3080c1]"></div>}
                  </div>
                </div>

                <p className="text-gray-700 mb-4">
                  Designed for teams, organizations, and leaders looking to upskill their workforce.
                </p>

                <ul className="space-y-2 mb-6">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#3080c1] mt-0.5 mr-2 flex-shrink-0" />
                    <span>Group training sessions</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#3080c1] mt-0.5 mr-2 flex-shrink-0" />
                    <span>Custom implementation strategies</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#3080c1] mt-0.5 mr-2 flex-shrink-0" />
                    <span>Organizational AI transformation</span>
                  </li>
                </ul>

                <a
                  href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full block text-center py-3 rounded-lg transition duration-300
                    ${
                      userTrack === "enterprise"
                        ? "bg-[#3080c1] text-white hover:bg-[#246399]"
                        : "border border-[#3080c1] text-[#3080c1] hover:bg-[#3080c1] hover:text-white"
                    }`}
                >
                  Book Discovery Call
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* DISCOVERY SPARK SECTION */}
        <section
          id="demo"
          className="py-16 px-4 sm:px-8 bg-gradient-to-br from-[#0e1d34] to-[#1e3a8a] text-white"
          ref={(el) => registerSectionRef("demo", el)}
        >
          <div className="max-w-6xl mx-auto text-center">
            <h2 className={`text-3xl font-bold mb-4 ${isVisible["demo"] ? "animate-fadeIn" : "opacity-0"}`}>
              Want to know how powerful AI can get when it thinks like you?
            </h2>
            <p
              className={`text-lg text-blue-200 max-w-3xl mx-auto mb-10 ${isVisible["demo"] ? "animate-fadeIn" : "opacity-0"}`}
              style={{ animationDelay: "0.2s" }}
            >
              See how our structured approach transforms vague ideas into powerful, actionable systems
            </p>

            {/* Demo Carousel */}
            <div
              className={`max-w-2xl mx-auto mb-10 ${isVisible["demo"] ? "animate-fadeInUp" : "opacity-0"}`}
              style={{ animationDelay: "0.3s" }}
            >
              <div className="bg-[#0e1d34]/50 backdrop-blur-sm p-6 rounded-xl border border-[#3080c1]/30">
                <div className="flex justify-between items-center mb-6">
                  {demoSlides.map((slide, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`flex-1 py-2 text-sm font-medium transition-all ${
                        currentSlide === index
                          ? "text-white border-b-2 border-[#3080c1]"
                          : "text-blue-200 border-b-2 border-transparent hover:text-white"
                      }`}
                    >
                      {slide.title}
                    </button>
                  ))}
                </div>

                <div className="min-h-[200px] flex items-center justify-center">{demoSlides[currentSlide].content}</div>

                <div className="flex justify-center mt-6 space-x-2">
                  {demoSlides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-all ${
                        currentSlide === index ? "bg-[#3080c1]" : "bg-white/30"
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>

              <div className="text-center mt-6">
                <p className="text-blue-200 mb-2">This took 90 seconds. You'll learn to do this in 10.</p>
                <a
                  href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-[#3080c1] hover:bg-[#246399] text-white px-8 py-3 rounded-lg text-lg font-bold tracking-wide transition duration-300"
                >
                  𝗜 𝗪𝗮𝗻𝘁 𝗧𝗵𝗶𝘀 𝗣𝗼𝘄𝗲𝗿
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* COURSE HIGHLIGHTS */}
        <section
          id="what-youll-gain"
          className="py-16 px-4 sm:px-8 bg-white text-[#0e1d34]"
          ref={(el) => registerSectionRef("what-youll-gain", el)}
        >
          <div className="max-w-6xl mx-auto">
            <h2
              className={`text-3xl font-bold mb-4 text-center ${isVisible["what-youll-gain"] ? "animate-fadeIn" : "opacity-0"}`}
            >
              You'll Think Clearer, Faster, Freer
            </h2>
            <p
              className={`text-lg text-gray-700 max-w-3xl mx-auto mb-10 text-center ${isVisible["what-youll-gain"] ? "animate-fadeIn" : "opacity-0"}`}
              style={{ animationDelay: "0.2s" }}
            >
              Direct insights from the course that transform how you'll work with AI
            </p>

            <div
              className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${isVisible["what-youll-gain"] ? "animate-fadeInUp" : "opacity-0"}`}
              style={{ animationDelay: "0.3s" }}
            >
              {courseHighlights.map((highlight, i) => (
                <div
                  key={i}
                  className="bg-[#f0f4f9] p-6 rounded-xl shadow border border-gray-200 hover:shadow-lg transition"
                >
                  <div className="mb-4">{highlight.icon}</div>
                  <blockquote className="italic text-gray-700">"{highlight.text}"</blockquote>
                </div>
              ))}
            </div>

            <div
              className={`mt-8 text-center ${isVisible["what-youll-gain"] ? "animate-fadeIn" : "opacity-0"}`}
              style={{ animationDelay: "0.5s" }}
            >
              <p className="text-[#3080c1] font-semibold flex items-center justify-center gap-2">
                <Flame className="w-5 h-5" /> These are just the sparks—inside, you'll build the fire.
              </p>
            </div>
          </div>
        </section>

        {/* COURSE MODULES - LOCKED PREVIEW */}
        <section
          id="modules"
          className="py-16 px-4 sm:px-8 bg-[#f0f4f9] text-[#0e1d34]"
          ref={(el) => registerSectionRef("modules", el)}
        >
          <div className="max-w-6xl mx-auto">
            <h2
              className={`text-3xl font-bold mb-6 text-center ${isVisible["modules"] ? "animate-fadeIn" : "opacity-0"}`}
            >
              Course Modules
            </h2>
            <p
              className={`text-lg text-gray-700 max-w-3xl mx-auto mb-10 text-center ${isVisible["modules"] ? "animate-fadeIn" : "opacity-0"}`}
              style={{ animationDelay: "0.2s" }}
            >
              A comprehensive journey from AI fundamentals to mastery
            </p>

            <div
              className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${isVisible["modules"] ? "animate-fadeInUp" : "opacity-0"}`}
              style={{ animationDelay: "0.3s" }}
            >
              {courseModules.map((module, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-200 relative group"
                >
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-[#3080c1]/10 p-3 rounded-md">{module.icon}</div>
                      <div>
                        <h3 className="text-xl font-bold text-[#0e1d34]">{module.title}</h3>
                        <p className="text-gray-600 mt-1">{module.description}</p>
                      </div>
                    </div>

                    {/* Locked Content Preview */}
                    <div className="mt-6 pl-16 relative">
                      <div className="absolute inset-0 bg-white/80 backdrop-blur-[2px] flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <Lock className="w-8 h-8 text-[#3080c1] mb-2" />
                        <p className="text-[#0e1d34] font-medium text-center">Unlock inside the course</p>
                      </div>

                      <h4 className="text-sm font-semibold text-[#3080c1] uppercase tracking-wider mb-3">
                        Topics Covered
                      </h4>
                      <div className="space-y-2 blur-[2px]">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                        <div className="h-4 bg-gray-200 rounded w-4/5"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHO IT'S FOR */}
        <section
          id="who"
          className="py-16 px-4 sm:px-8 bg-white text-[#0e1d34]"
          ref={(el) => registerSectionRef("who", el)}
        >
          <div className="max-w-6xl mx-auto">
            <h2 className={`text-3xl font-bold mb-6 text-center ${isVisible["who"] ? "animate-fadeIn" : "opacity-0"}`}>
              Who This Is For
            </h2>
            <p
              className={`text-lg text-gray-700 max-w-3xl mx-auto mb-10 text-center ${isVisible["who"] ? "animate-fadeIn" : "opacity-0"}`}
              style={{ animationDelay: "0.2s" }}
            >
              This course is designed for those ready to transform their relationship with AI
            </p>

            <div
              className={`grid grid-cols-1 md:grid-cols-3 gap-6 ${isVisible["who"] ? "animate-fadeInUp" : "opacity-0"}`}
              style={{ animationDelay: "0.3s" }}
            >
              <div className="p-6 bg-[#3080c1]/10 rounded-xl border border-[#3080c1]/30">
                <Compass className="mb-4 text-[#3080c1] w-8 h-8" />
                <h3 className="text-xl font-semibold mb-2">Thinkers</h3>
                <p className="text-sm text-gray-700">
                  Strategic minds, creators, and visionaries seeking to systemize clarity.
                </p>
              </div>
              <div className="p-6 bg-[#3080c1]/10 rounded-xl border border-[#3080c1]/30">
                <Hammer className="mb-4 text-[#3080c1] w-8 h-8" />
                <h3 className="text-xl font-semibold mb-2">Builders</h3>
                <p className="text-sm text-gray-700">
                  Developers and technologists crafting modular, AI-driven solutions.
                </p>
              </div>
              <div className="p-6 bg-[#3080c1]/10 rounded-xl border border-[#3080c1]/30">
                <Users className="mb-4 text-[#3080c1] w-8 h-8" />
                <h3 className="text-xl font-semibold mb-2">Leaders</h3>
                <p className="text-sm text-gray-700">
                  Operators, executives, and project leads scaling autonomy through AI.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS - ANIMATED */}
        <section
          id="testimonials"
          className="py-16 px-4 sm:px-8 bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34] text-white"
          ref={(el) => registerSectionRef("testimonials", el)}
        >
          <div className="max-w-6xl mx-auto">
            <h2
              className={`text-3xl font-bold mb-10 text-center ${isVisible["testimonials"] ? "animate-fadeIn" : "opacity-0"}`}
            >
              What Our Students Say
            </h2>

            {/* Featured Testimonial */}
            <div
              className={`mb-12 ${isVisible["testimonials"] ? "animate-fadeInUp" : "opacity-0"}`}
              style={{ animationDelay: "0.2s" }}
            >
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20 text-center max-w-3xl mx-auto">
                <div className="mb-6 text-[#3080c1] flex justify-center">
                  <Sparkles className="w-12 h-12" />
                </div>
                <blockquote className="text-2xl font-light italic text-white mb-6">
                  "I've missed a lot of damn rides and I'm not missing this one. I see it, and I'm not missing it."
                </blockquote>
                <div className="flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-[#3080c1]/30 mr-3 flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#3080c1]" />
                  </div>
                  <p className="text-lg font-semibold text-white">Jason, Course Participant</p>
                </div>
              </div>
            </div>

            <div
              className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${isVisible["testimonials"] ? "animate-fadeInUp" : "opacity-0"}`}
              style={{ animationDelay: "0.4s" }}
            >
              {testimonialQuotes.slice(1).map((testimonial, i) => (
                <div key={i} className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                  <div className="mb-4 text-[#3080c1]">
                    <Sparkles className="w-8 h-8" />
                  </div>
                  <blockquote className="italic text-blue-100 mb-4">"{testimonial.quote}"</blockquote>
                  <p className="text-sm font-semibold text-white">{testimonial.author}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* THE METHOD - SIMPLIFIED */}
        <section
          className="py-16 px-4 sm:px-8 bg-[#f0f4f9] text-[#0e1d34]"
          ref={(el) => registerSectionRef("method", el)}
        >
          <div className="max-w-6xl mx-auto text-center">
            <h2 className={`text-3xl font-bold mb-6 ${isVisible["method"] ? "animate-fadeIn" : "opacity-0"}`}>
              Our Approach: Reflect → Structure → Command
            </h2>
            <p
              className={`text-lg text-gray-700 max-w-3xl mx-auto mb-10 ${isVisible["method"] ? "animate-fadeIn" : "opacity-0"}`}
              style={{ animationDelay: "0.2s" }}
            >
              A repeatable, learnable process for building AI systems that mirror your mind
            </p>

            <div
              className={`grid grid-cols-1 md:grid-cols-3 gap-6 text-left ${isVisible["method"] ? "animate-fadeInUp" : "opacity-0"}`}
              style={{ animationDelay: "0.3s" }}
            >
              <div className="p-6 bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34] rounded-xl border border-[#3080c1]/30 text-white">
                <MessageSquare className="w-8 h-8 text-[#3080c1] mb-4" />
                <h3 className="text-xl font-bold mb-2">Reflect</h3>
                <p className="text-blue-100 text-sm">
                  Clarify your goals, constraints, and values. Define what success looks like.
                </p>
              </div>
              <div className="p-6 bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34] rounded-xl border border-[#3080c1]/30 text-white">
                <Layers className="w-8 h-8 text-[#3080c1] mb-4" />
                <h3 className="text-xl font-bold mb-2">Structure</h3>
                <p className="text-blue-100 text-sm">Build prompt blueprints, reasoning flows, and modular logic.</p>
              </div>
              <div className="p-6 bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34] rounded-xl border border-[#3080c1]/30 text-white">
                <Zap className="w-8 h-8 text-[#3080c1] mb-4" />
                <h3 className="text-xl font-bold mb-2">Command</h3>
                <p className="text-blue-100 text-sm">
                  Operate the system live. Automate actions. Scale with confidence and clarity.
                </p>
              </div>
            </div>

            <div
              className={`mt-8 ${isVisible["method"] ? "animate-fadeIn" : "opacity-0"}`}
              style={{ animationDelay: "0.5s" }}
            >
              <a
                href="/CourseFlow/coming-soon"
                className="inline-flex items-center text-[#3080c1] hover:text-[#246399] font-medium"
              >
                <span>See how we build this?</span>
                <ArrowRight className="ml-2 w-4 h-4" />
                <span className="ml-2 text-sm text-gray-500">Join the course</span>
              </a>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
       <section
          id="reserve"
          className="py-20 px-4 sm:px-8 bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34] text-white text-center"
          ref={(el) => registerSectionRef("reserve", el)}
        >
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">
              Prepare Your Organization for the Post-AI Economy
            </h2>

            <p className="text-lg text-blue-200 mb-8">
              This isn’t training. It’s transformation. Align your team around systems thinking,
              prompt strategy, and AI-driven decision design.
            </p>

            <a
              href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#3080c1] hover:bg-[#246399] text-white px-8 py-4 rounded-lg text-lg font-medium transition-all duration-300 hover:scale-105"
            >
              📞 Book a Strategic Training Call
            </a>

            <p className="mt-6 text-blue-200 font-medium max-w-xl mx-auto">
              We’ll audit your workflows, identify AI leverage points, and co-design a roadmap your team can actually follow.
              This course will shift how they think—forever.
            </p>
          </div>
        </section>
      </main>

      <Footer />

      {/* Back to top button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="fixed bottom-16 right-6 bg-[#3080c1] hover:bg-[#246399] text-white p-3 rounded-full shadow-lg transition-all duration-300 z-40"
        aria-label="Back to top"
      >
        <ChevronUp className="w-5 h-5" />
      </button>

      {/* Scroll Progress Indicator */}
      <div className="fixed bottom-0 left-0 w-full h-1 bg-gray-200 z-50">
        <div
          className="h-full bg-[#3080c1]"
          style={{
            width:
              typeof window !== "undefined"
                ? `${(window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100}%`
                : "0%",
          }}
        ></div>
      </div>
    </div>
  )
}

export default MyteAITrainingCourses
